import { useQuery } from '@tanstack/react-query';
import { SUBSCRIPTION_KEYS } from '../constants/queryKeys';
import { paymentService, SubscriptionDetails } from '../services/paymentService';
import { uiLogger } from '../utils/loggerInstance';
import { useProfile } from './useProfile';
import { useOrganization } from './useOrganization';

/**
 * Single hook for "current subscription" used by access/plan checks (e.g. RequireBasePlanRoute).
 * Calls exactly one API: subscriptions/current (personal) OR subscriptions/enterprise/current (org),
 * based on organization (GET /organizations/me). Never calls both.
 */
export function useCurrentSubscriptionForAccess() {
  const {
    isLoading: profileLoading,
    isError: profileError,
    error: profileQueryError,
  } = useProfile();

  const {
    data: organization,
    isLoading: orgLoading,
    isError: orgError,
    error: orgQueryError,
  } = useOrganization();

  const isOrgUser = !!organization;
  const orgReady = !orgLoading && !orgError;

  const {
    data: subscription,
    isLoading: subscriptionLoading,
    error: subscriptionError,
    refetch: refetchSubscription,
  } = useQuery<SubscriptionDetails | null>({
    queryKey: SUBSCRIPTION_KEYS.forAccess(isOrgUser),
    enabled: orgReady,
    queryFn: async () => {
      try {
        if (isOrgUser) {
          uiLogger.info('Fetching enterprise subscription (org user)', {
            orgRole: organization?.role,
          });
          const data = await paymentService.getEnterpriseCurrentSubscription();
          uiLogger.info('Enterprise subscription fetched', { hasSubscription: !!data });
          return data;
        }
        uiLogger.info('Fetching personal subscription (no org)');
        const data = await paymentService.getCurrentSubscription();
        uiLogger.info('Personal subscription fetched', { hasSubscription: !!data });
        return data;
      } catch (error) {
        const context = isOrgUser ? 'enterprise' : 'personal';
        uiLogger.error(`Failed to fetch ${context} subscription`, { error });
        throw error;
      }
    },
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000,
  });

  return {
    subscription: orgReady ? subscription : undefined,
    isOrgUser,
    organizationRole: organization?.role ?? null,
    isLoading: profileLoading || orgLoading || (orgReady && subscriptionLoading),
    error: subscriptionError,
    refetch: refetchSubscription,
    profileError: profileError ? profileQueryError : undefined,
    organizationError: orgError ? orgQueryError : undefined,
    accountContextError: profileError ? profileQueryError : orgError ? orgQueryError : undefined,
  };
}
